import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  ArrowLeft,
  Image as ImageIcon,
  Hash,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { GetSingleBannersThunk } from "../../redux/states/banners/thunk";

const ViewBanners = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleBanner, loading } = useSelector((state) => state?.banners);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    dispatch(GetSingleBannersThunk(uuid));
  }, [dispatch, uuid]);

  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-semibold text-blue-600">
              Loading product details...
            </p>
            <p className="text-gray-500 mt-2">Please wait</p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  if (!singleBanner) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
          <div className="text-center bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Product Not Found
            </h2>
            <p className="text-gray-600 mb-6">
              The product you are looking for is not available.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 mx-auto transition-all duration-300 transform hover:scale-105"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Product List
            </button>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return {
          color: "bg-green-100 text-green-800 border-green-200",
          icon: CheckCircle,
        };
      case "inactive":
        return {
          color: "bg-red-100 text-red-800 border-red-200",
          icon: XCircle,
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800 border-gray-200",
          icon: AlertCircle,
        };
    }
  };

  const StatusBadge = getStatusBadge(singleBanner.status);
  const StatusIcon = StatusBadge.icon;

  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-12xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-4 lg:mb-0">
                <div className="flex items-center gap-3 mb-3">
                  <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors p-2 rounded-lg hover:bg-blue-50"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h3 className="text-3xl font-bold text-gray-800">
                    {singleBanner.name}
                  </h3>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold border flex items-center gap-1 ${StatusBadge.color}`}
                  >
                    <StatusIcon className="w-4 h-4" />
                    {singleBanner.status}
                  </span>
                </div>
                <p className="text-gray-600 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  SKU: {singleBanner.sku} | UUID: {singleBanner.uuid}
                </p>
              </div>
            </div>
              {/* Banner Banner */}
              <div className="bg-white rounded-2xl  border-gray-200 p-1">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-blue-600" />
                  Banner Image
                </h2>
                {singleBanner.image ? (
                  <div className="relative">
                    <img
                      src={`${API_URL}/${singleBanner.image}`}
                      alt={singleBanner.name}
                      className="w-full h-64 object-cover rounded-xl border-2 border-gray-200 shadow-sm"
                    />
                    <div className="absolute top-3 right-3 bg-black bg-opacity-50 text-white px-2 py-1 rounded-lg text-sm">
                      Featured
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-64 flex flex-col items-center justify-center bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 text-gray-400">
                    <ImageIcon className="w-16 h-16 mb-2" />
                    <p>No Image Available</p>
                  </div>
                )}
              </div>
          </div>
        </div>
        <div className="max-w-12xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-2">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  Title: {singleBanner.title || "Not Define"}
                </h2>
                <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  Tagline: {singleBanner.tagline || "Not Define"}
                </p>
                <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  Banner Link: {singleBanner.button_link || "Not Define"}
                </p>
                <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  Sub Title: {singleBanner.sub_title || "Not Define"}
                </p>
                <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  Sort Description:{" "}
                  {singleBanner.sort_description || "Not Define"}
                </p>
                <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  Description: {singleBanner.description || "Not Define"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </NonAuthLayout>
  );
};

/* -------------------- Helper Components -------------------- */

export default ViewBanners;
